import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useMemo } from 'react';
import { getBatchesForNode, getBatchStatus, isBatchDone, mergeBatches } from './batch.utils';
import type { BatchRun } from '@/types/batch.types';

export type BatchesState = {
  batches: Record<string, BatchRun>;
  updateBatches: (batches: Record<string, BatchRun>) => Record<string, BatchRun>;
  isTaskManagerOpen: boolean;
  setIsTaskManagerOpen: (isOpen: boolean) => void;
  clearBatch: (bid: string) => void;
  clearAllBatches: () => void;
  /** Forgets every batch, for leaving a workflow. */
  resetBatches: () => void;
  markBatchesAsViewed: () => void;
  expectedRunCounts: Record<string, number>;
  setExpectedRunCount: (batchId: string, count: number) => void;
};

export const useBatchesStore = create<BatchesState>()(
  devtools(
    (set) => ({
      isTaskManagerOpen: false,
      setIsTaskManagerOpen: (isOpen: boolean) => set({ isTaskManagerOpen: isOpen }),
      batches: {},
      expectedRunCounts: {},
      setExpectedRunCount: (batchId: string, count: number) =>
        set((state) => ({ expectedRunCounts: { ...state.expectedRunCounts, [batchId]: count } })),
      updateBatches: (batches: Record<string, BatchRun>) => {
        let mergedIncomingBatches: Record<string, BatchRun> = {};
        set((state) => {
          const mergedBatches = mergeBatches(state.batches, batches);
          mergedIncomingBatches = Object.fromEntries(Object.keys(batches).map((bid) => [bid, mergedBatches[bid]]));
          return { batches: mergedBatches };
        });
        return mergedIncomingBatches;
      },
      clearBatch: (bid: string) =>
        set((state) => {
          const { [bid]: _, ...remainingExpectedRunCounts } = state.expectedRunCounts;
          return {
            batches: { ...state.batches, [bid]: { ...state.batches[bid], isClearedInTaskManager: true } },
            expectedRunCounts: remainingExpectedRunCounts,
          };
        }),
      clearAllBatches: () =>
        set((state) => {
          const updatedBatches = Object.fromEntries(
            Object.entries(state.batches).map(([bid, batch]) => [
              bid,
              { ...batch, isClearedInTaskManager: isBatchDone(batch) },
            ]),
          );
          const remainingExpectedRunCounts = Object.fromEntries(
            Object.entries(state.expectedRunCounts).filter(([bid]) => {
              const batch = state.batches[bid];
              return !!batch && !isBatchDone(batch);
            }),
          );
          return { batches: updatedBatches, expectedRunCounts: remainingExpectedRunCounts };
        }),
      resetBatches: () => set({ batches: {}, expectedRunCounts: {} }),
      markBatchesAsViewed: () =>
        set((state) => ({
          batches: Object.fromEntries(
            Object.entries(state.batches).map(([bid, batch]) => [bid, { ...batch, isViewed: true }]),
          ),
        })),
    }),
    {
      name: 'Batches Store',
      enabled: import.meta.env.DEV,
    },
  ),
);

export const useBatches = (): Record<string, BatchRun> => {
  return useBatchesStore((state) => state.batches);
};

export const useUpdateBatches = (): ((batches: Record<string, BatchRun>) => Record<string, BatchRun>) => {
  return useBatchesStore((state) => state.updateBatches);
};

export const useBatchesByRecipeId = (recipeId: string): BatchRun[] => {
  const batches = useBatches();
  const recipeBatches = useMemo(
    () => Object.values(batches).filter((batch) => batch.recipeId === recipeId),
    [batches, recipeId],
  );
  return recipeBatches;
};

export const useGetActiveBatches = (recipeId: string): BatchRun[] => {
  const batches = useBatchesByRecipeId(recipeId);
  return useMemo(() => Object.values(batches).filter((batch) => !isBatchDone(batch)), [batches]);
};

/** Whether this workflow has a run that hasn't finished; re-renders only when that changes. */
export const useHasRunInProgress = (workflowId: string | undefined): boolean =>
  useBatchesStore((state) =>
    Object.values(state.batches).some((batch) => batch.recipeId === workflowId && !isBatchDone(batch)),
  );

export const useGetActiveBatchesWithActiveNode = (nodeId: string): BatchRun[] => {
  const batches = useRunningBatchesByNodeId(nodeId);
  return useMemo(
    () =>
      batches.filter(
        (batch) =>
          // Queued: the node run that will report progress doesn't exist yet, but the node is spoken for.
          getBatchStatus(batch) === 'QUEUED' ||
          batch.recipeRuns.some((run) =>
            run.nodeRuns.some(
              (nodeRun) => nodeRun.nodeId === nodeId && (nodeRun.status === 'RUNNING' || nodeRun.status === 'PENDING'),
            ),
          ),
      ),
    [batches, nodeId],
  );
};

export const useBatchesByNodeId = (nodeId: string): BatchRun[] => {
  const batches = useBatches();
  return useMemo(() => getBatchesForNode(nodeId, Object.values(batches)), [batches, nodeId]);
};

export const useRunningBatchesByNodeId = (nodeId: string): BatchRun[] => {
  const batches = useBatchesByNodeId(nodeId);
  return useMemo(() => batches.filter((batch) => !isBatchDone(batch)), [batches]);
};

export const useIsNodeInRunningBatch = (nid: string): boolean => {
  const batches = useRunningBatchesByNodeId(nid);
  return useMemo(() => batches.length > 0, [batches]);
};

export const useRunningBatchesByRecipeId = (recipeId: string): BatchRun[] => {
  const batches = useBatchesByRecipeId(recipeId);
  return useMemo(() => batches.filter((batch) => !isBatchDone(batch)), [batches]);
};

export const useExpectedRunCounts = (): Record<string, number> => {
  return useBatchesStore((state) => state.expectedRunCounts);
};

export const useSetExpectedRunCount = (): ((batchId: string, count: number) => void) => {
  return useBatchesStore((state) => state.setExpectedRunCount);
};

export const useAnyBatchDone = (recipeId: string): BatchRun[] => {
  const batches = useBatchesByRecipeId(recipeId);
  return useMemo(() => batches.filter(isBatchDone), [batches]);
};
